import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize2 } from "lucide-react";
import { GALLERY } from "@/data/site";
import { Reveal } from "@/lib/motionPrimitives";

const CATEGORIES = ["All", "Steel Structures", "Storage Tanks", "Pipelines"];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const items = useMemo(
    () => (filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter)),
    [filter]
  );

  return (
    <section id="gallery" className="relative bg-surface py-24 md:py-32" data-testid="gallery">
      <div className="container-x">
        <Reveal>
          <p className="overline">Real Results</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Before &amp; After Gallery
          </h2>
          <p className="mt-4 max-w-xl text-muted-foreground">
            Slide-worthy transformations — from heavy corrosion and rust to a durable, protective finish.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-3" data-testid="gallery-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`gallery-filter-${c}`}
              className={`rounded-full border px-5 py-2 text-sm font-medium font-display transition-colors ${
                filter === c
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:border-primary hover:text-primary"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 grid gap-6 md:grid-cols-2">
          <AnimatePresence mode="popLayout">
            {items.map((g) => (
              <motion.figure
                layout
                key={g.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4 }}
                data-testid={`gallery-item-${g.id}`}
                onClick={() => setActive(g)}
                className="group relative cursor-pointer overflow-hidden rounded-3xl border border-border bg-background"
              >
                <img
                  src={g.image}
                  alt={g.title}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <figcaption className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/80 to-transparent p-5">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-primary-foreground/70">{g.category}</p>
                    <p className="font-display font-semibold text-white">{g.title}</p>
                  </div>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur">
                    <Maximize2 className="h-4 w-4" />
                  </span>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white"
              onClick={() => setActive(null)}
              aria-label="Close"
              data-testid="lightbox-close"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-5xl overflow-hidden rounded-2xl"
            >
              <img src={active.image} alt={active.title} className="max-h-[85vh] w-full object-contain" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
